import { SetStateAction, useEffect, useState } from "react";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Form, Spinner, Table } from "react-bootstrap";
import PaginationComponent from "../../components/PaginationComponent";
import FormInput from "../../components/FormInput";
import { Wallet } from "./UserWalletDetails";
import backBtn from "../../assets/icons/arrow_left.svg";
import moment from "moment";

interface UserWalletTransaction {
	_id: string,
	walletId: string,
	reference: string,
	product: string,
	type: string,
	target: string,
	description: string,
	amount: number,
	charge: number,
	previousBalance: number,
	newBalance: number,
	user: string,
	createdAt: string
};

interface UserWalletActivitiesProps {
	onHide: () => void;
	userId: string;
	formatAmount: (amount: number) => string;
	walletDetail: Wallet | null;
}

const UserWalletActivities = ({ onHide, userId, formatAmount, walletDetail }: UserWalletActivitiesProps) => {

	const [walletTransactions, setWalletTransactions] = useState<UserWalletTransaction[]>([]);
	const [selectedType, setSelectedType] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({
		currentPage: 1,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
		nextPage: null,
		previousPage: null
	});

	const handlePaginate = (pageNumber: number) => {
		setPaginationData((prevData) => ({ ...prevData, currentPage: pageNumber }));
	};

	const formatDate = (dateString: moment.MomentInput) => {
		return moment(dateString).format('DD-MM-YYYY');
	};

	const handleStartDateChange = (e: { target: { value: SetStateAction<string>; }; }) => {
		setStartDate(e.target.value);
	};

	const handleEndDateChange = (e: { target: { value: SetStateAction<string>; }; }) => {
		setEndDate(e.target.value);
	};

	const fetchUserWalletActivities = async () => {
		setIsLoading(true);
		try {
			api.setAuthorization(api.getLoggedInUser());
			const queryParams = new URLSearchParams({ page: paginationData.currentPage?.toString() || '1' });
			if (selectedType) {
				queryParams.set('type', selectedType);
			};
			if (startDate) {
				queryParams.set('startDate', moment(startDate).format('YYYY-MM-DD'));
			}
			if (endDate) {
				queryParams.set('endDate', moment(endDate).format('YYYY-MM-DD'));
			}

			const response = await api.get(endpoints.getUserWalletTransactions(userId), queryParams);
			setWalletTransactions(response.data.data.docs);
			setPaginationData({
				currentPage: response.data.data.page,
				totalPages: response.data.data.totalPages,
				hasNextPage: response.data.data.hasNextPage,
				hasPreviousPage: response.data.data.hasPreviousPage,
				nextPage: response.data.data.nextPage,
				previousPage: response.data.data.previousPage
			});
			setIsLoading(false);
		} catch (error) {
			console.error("Error fetching transactions:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchUserWalletActivities();
	}, [paginationData.currentPage, selectedType, startDate, endDate]);

	const capitalizeFirstLetter = (string: string) => {
		if (!string) return '';
		return string.charAt(0).toUpperCase() + string.slice(1);
	};


	return (
		<>
			<div className="wallet-user">
				<img src={backBtn} alt="back" onClick={onHide} style={{ cursor: 'pointer' }} />
				<div>
					<p className="mb-0">{`${walletDetail?.user?.firstName} ${walletDetail?.user?.lastName}`}</p>
					<span>{capitalizeFirstLetter(walletDetail?.user?.userType)}</span>
				</div>
			</div>
			<div className="border"></div>
			<div className="table-topbar">
				<h4>Activities</h4>
				<div style={{display:'flex', alignItems:'center', gap: '40px'}}>
					<div className="select-type mt-4">
						<Form.Select
							name="type"
							title="type"
							onChange={e => setSelectedType((e.target as HTMLSelectElement).value)}
						>
							<option value="">Type</option>
							<option value="credit">Credit</option>
							<option value="debit">Debit</option>
						</Form.Select>
					</div>
					<div className="range-input-section mb-3 mt-2">
						<FormInput
							label="From:"
							type="date"
							name="startDate"
							value={startDate}
							onChange={handleStartDateChange}
						/>
						<FormInput
							label="To:"
							type="date"
							name="endDate"
							value={endDate}
							onChange={handleEndDateChange}
						/>
					</div>
				</div>
			</div>
			<div className="table-body">
				<Table>
					{isLoading ? (
						<div className="spinner-div">
							<Spinner animation="border" size="sm" role="status" />
						</div>
					) : walletTransactions.length > 0 ? (
						<>
							<thead>
								<tr>
									<th className="custom-thead-bg">Activity</th>
									<th className="custom-thead-bg">Amount</th>
									<th className="custom-thead-bg">Date</th>
									<th className="custom-thead-bg">Status</th>
								</tr>
							</thead>
							<tbody>
								{walletTransactions.map((walletTransaction, index) => (
									<tr key={walletTransaction._id}>
										<td>{walletTransaction.description}</td>
										<td>{formatAmount(walletTransaction.amount)}</td>
										<td>{formatDate(walletTransaction.createdAt)}</td>
									</tr>
								))}
							</tbody>

						</>
					) : (
						<div className="no-results">
							No activities found.
						</div>
					)}
				</Table>
			</div>

			<PaginationComponent
				currentPage={paginationData.currentPage}
				totalPages={paginationData.totalPages}
				hasNextPage={paginationData.hasNextPage}
				hasPreviousPage={paginationData.hasPreviousPage}
				nextPage={paginationData.nextPage}
				previousPage={paginationData.previousPage}
				onPaginate={handlePaginate}
			/>
		</>
	)
}

export default UserWalletActivities;